// 옛 상점 장비 → 6부위 장비 전환(ECONOMY 14절, 2026-10-26 장비 통합). 데이터와 순수 계산만(three·DOM 없음).
// RPG-1 상점 무기·갑옷(shared/rpg ITEMS)은 이제 능력치·외형에 쓰지 않는다. 플레이어가 가진 옛 장비는 같은 능력치의
// "옛 장비" 개체로 gear.v1에 한 번 넣는다(무손실). 능력치는 아이템 레벨 곡선을 타지 않는 고정값(EquipDef.fixed),
// 아이템 레벨 1(분해·판매·강화 비용이 1레벨 값 — 전환으로 고레벨 재료를 얻는 길이 없다). 드롭·상점 후보에는 넣지 않는다.
import type { EquipDef, EquipSlot, Grade } from './types';
import type { GearItem } from '../gear';

/** 옛 상점 장비 id(shared/rpg ItemId와 같은 문자열 — 순환 import를 피해 값으로 둔다, legacyGear.test가 맞는지 본다) */
export type LegacyItemId = 'sword_worn' | 'sword_training' | 'sword_steel' | 'sword_ember' | 'armor_travel' | 'armor_chain' | 'armor_crimson';
export const LEGACY_ITEM_IDS: readonly LegacyItemId[] = ['sword_worn', 'sword_training', 'sword_steel', 'sword_ember', 'armor_travel', 'armor_chain', 'armor_crimson'];

interface Row { readonly id: LegacyItemId; readonly name: string; readonly slot: EquipSlot; readonly grade: Grade; readonly tier: 1 | 2 | 3; readonly attack?: number; readonly maxHp?: number; readonly crit?: number }
/** 옛 표(ECONOMY 10·11절, 2026-10-20 수치)의 능력치·외형 단계 그대로. 등급은 색만(고정값이라 배율을 곱하지 않는다) */
const ROWS: readonly Row[] = [
  { id: 'sword_worn', name: '카엘의 낡은 대검', slot: 'weapon', grade: 'common', tier: 1 },
  { id: 'sword_training', name: '훈련용 대검', slot: 'weapon', grade: 'common', tier: 1, attack: 10 },
  { id: 'sword_steel', name: '강철 대검', slot: 'weapon', grade: 'rare', tier: 2, attack: 30, crit: 0.03 },
  { id: 'sword_ember', name: '잿불 단조 대검', slot: 'weapon', grade: 'epic', tier: 3, attack: 80, crit: 0.05 },
  { id: 'armor_travel', name: '여행자 갑옷', slot: 'armor', grade: 'common', tier: 1, maxHp: 40 },
  { id: 'armor_chain', name: '사슬 갑옷', slot: 'armor', grade: 'rare', tier: 2, maxHp: 120 },
  { id: 'armor_crimson', name: '진홍 룬 갑옷', slot: 'armor', grade: 'epic', tier: 3, maxHp: 300 },
];

export function legacyDefId(id: LegacyItemId): string { return 'eq_legacy_' + id; }
export function legacyUid(id: LegacyItemId): string { return 'legacy-' + id; }

/** 옛 장비 정의(무기는 카엘 전용 — 옛 대검은 카엘 무기였다) */
export const LEGACY_EQUIPS: readonly EquipDef[] = ROWS.map((r) => ({
  id: legacyDefId(r.id), name: r.name, slot: r.slot, heroLock: r.slot === 'weapon' ? 'kael' as const : undefined,
  grade: r.grade, level: 1, region: 1 as const,
  base: { attack: r.attack ?? 0, maxHp: r.maxHp ?? 0, def: 0, crit: r.crit ?? 0 },
  affixPool: [], sockets: 0 as const, tier: r.tier, fixed: true,
}));
const ROW_BY_ID = new Map<string, Row>(ROWS.map((r) => [r.id, r]));
export function isLegacyItemId(id: string): id is LegacyItemId { return ROW_BY_ID.has(id); }

/** 옛 장비 한 개 → 개체(uid 고정이라 같은 옛 장비는 두 번 생기지 않는다). 옵션·소켓 없음, 강화 0 */
export function legacyGearItem(id: LegacyItemId): GearItem {
  const r = ROW_BY_ID.get(id)!;
  return { uid: legacyUid(id), def: legacyDefId(id), grade: r.grade, ilvl: 1, affixes: [], enh: 0, pity: new Array(16).fill(0) };
}

export interface LegacyPlanIn {
  /** profile.v2 장부가 가진 옛 장비(개선으로 사라진 원본은 이미 빠진 목록) */
  readonly owned: readonly string[];
  /** profile.v2 장착 레지스터의 무기·갑옷 */
  readonly weapon: string | null;
  readonly armor: string | null;
  /** 이미 전환한 옛 id(profile.v2 선택 필드 gearUnified) */
  readonly done: readonly string[];
  /** gear.v1에 이미 있는 개체 uid(가방 + 장착) */
  readonly uids: ReadonlySet<string>;
  /** gear.v1 장착 칸이 비어 있는가 */
  readonly weaponEmpty: boolean;
  readonly armorEmpty: boolean;
}
export interface LegacyPlan {
  /** 새로 넣을 옛 id(정렬) */
  readonly add: readonly LegacyItemId[];
  /** 넣고 바로 장착할 옛 id(빈 칸이었던 부위만) */
  readonly equip: readonly LegacyItemId[];
  /** 전환을 마친 뒤 profile.v2 gearUnified에 남길 목록(done ∪ owned 중 옛 id) */
  readonly done: readonly LegacyItemId[];
}

/**
 * 전환 계획(순수). 낡은 대검은 처음부터 가진 장비라 장부 소유 목록에 없어도 넣는다(무기 칸이 비지 않게).
 * done에 있거나 같은 uid가 이미 있으면 넣지 않는다 — 다시 로드·탭 두 개·롤백 뒤 복귀에서 중복이 없다.
 */
export function legacyPlan(p: LegacyPlanIn): LegacyPlan {
  const owned = new Set<LegacyItemId>(['sword_worn']);
  for (const id of p.owned) if (isLegacyItemId(id)) owned.add(id);
  const done = new Set<string>(p.done);
  const add: LegacyItemId[] = [];
  for (const id of LEGACY_ITEM_IDS) if (owned.has(id) && !done.has(id) && !p.uids.has(legacyUid(id))) add.push(id);
  const equip: LegacyItemId[] = [];
  // 무기: 옛 장착 무기를 넣는다(없거나 모르면 낡은 대검). 갑옷: 옛 장착 갑옷만
  const w = p.weapon && isLegacyItemId(p.weapon) && owned.has(p.weapon) ? p.weapon : 'sword_worn';
  if (p.weaponEmpty && add.includes(w)) equip.push(w);
  if (p.armorEmpty && p.armor && isLegacyItemId(p.armor) && add.includes(p.armor)) equip.push(p.armor);
  const all = LEGACY_ITEM_IDS.filter((id) => owned.has(id) || done.has(id));
  return { add, equip, done: all };
}
